import { Injectable } from '@angular/core';
import { AccessRightType, ApplicationSettingsType, ApplicationSettingsTypeGroup } from '@fi-shared/types';
import { AppTextService } from '@fi-shared/app-text';
import { IApplicationSettingsTypeGroupLabels } from '@fi-app/role/role';
import { ApplicationSettingsModel } from '@fi-app/role/application-settings.model';
import { Observable, ReplaySubject } from 'rxjs';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { ApplicationSettingTypeOrder } from '@fi-app/role/application-setting-type-order';
import { isDefined } from '@fi-shared/utils';
import { RoleModel } from '@fi-app/role/role.model';
import { ExtendedRoleRightModel } from '@fi-app/role/extended-role-right.model';
import { ValidationErrors } from '@angular/forms';

@Injectable()
export class RoleHelperService {
    private isRoleChanged$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    readonly connectedApplicationSettings = [
        [ApplicationSettingsType.CoreLocal, ApplicationSettingsType.CoreCentral, ApplicationSettingsType.CoreGlobal],
        [
            ApplicationSettingsType.DocumentLocal,
            ApplicationSettingsType.DocumentCentral,
            ApplicationSettingsType.DocumentGlobal,
        ],
        [
            ApplicationSettingsType.MediaBankLocal,
            ApplicationSettingsType.MediaBankCentral,
            ApplicationSettingsType.MediaBankGlobal,
        ],
        [ApplicationSettingsType.PostLocal, ApplicationSettingsType.PostCentral, ApplicationSettingsType.PostGlobal],
        [ApplicationSettingsType.StatisticsLocal, ApplicationSettingsType.StatisticsCentral],
        [
            ApplicationSettingsType.ChecklistLocal,
            ApplicationSettingsType.ChecklistCentral,
            ApplicationSettingsType.ChecklistGlobal,
        ],
        [
            ApplicationSettingsType.ChecklistV2Local,
            ApplicationSettingsType.ChecklistV2Central,
            ApplicationSettingsType.ChecklistV2Global,
        ],
        [
            ApplicationSettingsType.ChecklistV2AuditLocal,
            ApplicationSettingsType.ChecklistV2AuditCentral,
            ApplicationSettingsType.ChecklistV2AuditGlobal,
        ],
        [
            ApplicationSettingsType.TaskV2Local,
            ApplicationSettingsType.TaskV2Central,
            ApplicationSettingsType.TaskV2Global,
        ],
        [
            ApplicationSettingsType.OnboardingLocal,
            ApplicationSettingsType.OnboardingCentral,
            ApplicationSettingsType.OnboardingGlobal,
        ],
        [ApplicationSettingsType.FormLocal, ApplicationSettingsType.FormCentral, ApplicationSettingsType.FormGlobal],
        [ApplicationSettingsType.BillingLocal, ApplicationSettingsType.BillingCentral],
    ];

    private readonly specialCaseApplicationSettingsTypes: ApplicationSettingsType[] = [
        ApplicationSettingsType.CheckListReports,
        ApplicationSettingsType.ChecklistV2Reports,
        ApplicationSettingsType.ChecklistV2AuditReports,
        ApplicationSettingsType.Statistics2,
        ApplicationSettingsType.FormReports,
        ApplicationSettingsType.Task,
        ApplicationSettingsType.TaskV2Reports,
        ApplicationSettingsType.ForumAdmin,
        ApplicationSettingsType.Chat,
        ApplicationSettingsType.OnboardingReports,
        ApplicationSettingsType.BalooELearning,
    ];

    constructor(private appTextService: AppTextService) {}

    setRoleDataChanged(value: boolean) {
        this.isRoleChanged$.next(value);
    }

    isRoleDataChanged$(): Observable<boolean> {
        return this.isRoleChanged$.asObservable();
    }

    getApplicationSettingName(applicationSettingType: ApplicationSettingsType): string {
        switch (applicationSettingType) {
            case ApplicationSettingsType.DocumentLocal:
                return 'access_level.local';
            case ApplicationSettingsType.DocumentCentral:
                return 'access_level.central';
            case ApplicationSettingsType.DocumentGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.MediaBankLocal:
                return 'access_level.local';
            case ApplicationSettingsType.MediaBankCentral:
                return 'access_level.central';
            case ApplicationSettingsType.MediaBankGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.PostLocal:
                return 'access_level.local';
            case ApplicationSettingsType.PostCentral:
                return 'access_level.central';
            case ApplicationSettingsType.PostGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.CoreLocal:
                return 'access_level.local';
            case ApplicationSettingsType.CoreCentral:
                return 'access_level.central';
            case ApplicationSettingsType.CoreGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.Appearance:
                return 'access_level.appearance_level';
            case ApplicationSettingsType.Tag:
                return 'role.tag_administration';
            case ApplicationSettingsType.LoginReminder:
                return 'role.login_reminder_level';

            case ApplicationSettingsType.Email:
                return 'access_level.email_level';
            case ApplicationSettingsType.StatisticsLocal:
                return 'access_level.statistics_level';
            case ApplicationSettingsType.StatisticsCentral:
                return 'role.statistics_central';
            /*case ApplicationSettingsType.CustomMindMapImport: // todo delete labels later
                return 'module.handbook_import';*/
            case ApplicationSettingsType.CustomFranchiseIntranetUpgrade:
                return 'access_level.upgrades_level';
            /*case ApplicationSettingsType.CustomImportSystemObjects: // todo delete labels later
                return 'module.organization_import';*/
            case ApplicationSettingsType.ConfidentialAgreement:
                return 'agreement.confidencial_agreement';
            case ApplicationSettingsType.SMS:
                return 'access_level.sms_level';
            case ApplicationSettingsType.ChecklistLocal:
                return 'access_level.local';
            case ApplicationSettingsType.ChecklistCentral:
                return 'access_level.central';
            case ApplicationSettingsType.ChecklistGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.ChecklistV2Local:
                return 'access_level.local';
            case ApplicationSettingsType.ChecklistV2Central:
                return 'access_level.central';
            case ApplicationSettingsType.ChecklistV2Global:
                return 'access_level.global';

            case ApplicationSettingsType.ChecklistV2AuditLocal:
                return 'access_level.local';
            case ApplicationSettingsType.ChecklistV2AuditCentral:
                return 'access_level.central';
            case ApplicationSettingsType.ChecklistV2AuditGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.ChecklistV2AuditReports:
                return 'reports';

            case ApplicationSettingsType.Forum:
                return 'access_level.forum_level';
            case ApplicationSettingsType.CheckListReports:
                return 'reports';
            case ApplicationSettingsType.ChecklistV2Reports:
                return 'reports';
            case ApplicationSettingsType.BillingLocal:
                return 'billing_local';
            case ApplicationSettingsType.BillingCentral:
                return 'billing_central';
            case ApplicationSettingsType.CustomerBilling:
                return 'billing.customer_billing';
            case ApplicationSettingsType.ADIntegration:
                return 'ad_integration.module_name';
            case ApplicationSettingsType.Statistics2:
                return 'access_level.statistics_level';
            case ApplicationSettingsType.FormLocal:
                return 'access_level.local';
            case ApplicationSettingsType.FormCentral:
                return 'access_level.central';
            case ApplicationSettingsType.FormGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.FormReports:
                return 'reports';
            case ApplicationSettingsType.Task:
                return 'access_level.tasks_level';

            case ApplicationSettingsType.TaskV2Local:
                return 'access_level.local';
            case ApplicationSettingsType.TaskV2Central:
                return 'access_level.central';
            case ApplicationSettingsType.TaskV2Global:
                return 'access_level.global';
            case ApplicationSettingsType.TaskV2Reports:
                return 'reports';

            case ApplicationSettingsType.OnboardingLocal:
                return 'access_level.local';
            case ApplicationSettingsType.OnboardingCentral:
                return 'access_level.central';
            case ApplicationSettingsType.OnboardingGlobal:
                return 'access_level.global';
            case ApplicationSettingsType.OnboardingReports:
                return 'reports';

            case ApplicationSettingsType.ForumAdmin:
                return 'access_level.forum_admin_level';
            case ApplicationSettingsType.Chat:
                return 'access_level.chat_level';
            case ApplicationSettingsType.Personalkollen:
                return 'access_level.presonalkollen_level';
            case ApplicationSettingsType.BalooELearning:
                return 'access_level.baloo_level';
            case ApplicationSettingsType.Planday:
                return 'access_level.planday_level';
        }
    }

    getApplicationSettingColor(accessRightType: AccessRightType): string {
        switch (accessRightType) {
            case AccessRightType.SuperAdmin:
                return 'purple';
            case AccessRightType.Admin:
                return 'red';
            case AccessRightType.Share:
            case AccessRightType.Write:
                return 'orange';
            case AccessRightType.Read:
                return 'green';
            default:
                return 'grey';
        }
    }

    private isSpecialCaseApplicationSettingType(applicationSettingType: ApplicationSettingsType): boolean {
        return this.specialCaseApplicationSettingsTypes.includes(applicationSettingType);
    }

    getAccessLevelName(applicationSettingType: ApplicationSettingsType, accessRightType: AccessRightType): string {
        const ast = ApplicationSettingsType,
            art = AccessRightType,
            titlesMap = {};

        this.specialCaseApplicationSettingsTypes.forEach((applicationSettingsType) => {
            titlesMap[applicationSettingsType] = {};
            if (applicationSettingsType === ast.BalooELearning) {
                // BalooELearning has separate access level texts
                titlesMap[applicationSettingsType][art.Admin] = 'baloo_access_level.administrator';
                titlesMap[applicationSettingsType][art.Share] = 'baloo_access_level.manager';
                titlesMap[applicationSettingsType][art.Read] = 'baloo_access_level.member';
            } else {
                titlesMap[applicationSettingsType][art.Admin] = 'access_level.global';
                titlesMap[applicationSettingsType][art.Share] = 'access_level.central';
                titlesMap[applicationSettingsType][art.Write] = 'access_level.local';
            }
        });

        if (
            titlesMap.hasOwnProperty(applicationSettingType) &&
            titlesMap[applicationSettingType].hasOwnProperty(accessRightType)
        )
            return titlesMap[applicationSettingType][accessRightType];

        switch (accessRightType) {
            case AccessRightType.SuperAdmin:
                return 'access_level.super_admin';
            case AccessRightType.Admin:
                return 'common.administrate';
            case AccessRightType.Share:
                return 'common.share';
            case AccessRightType.Write:
                return 'common.write';
            case AccessRightType.Read:
                return 'common.read';
            case AccessRightType.UnAvailable:
                return 'common.disabled';
        }
    }

    getAccessRightTypeGroup(applicationSettingType: ApplicationSettingsType): ApplicationSettingsTypeGroup {
        switch (applicationSettingType) {
            case ApplicationSettingsType.CoreLocal:
            case ApplicationSettingsType.CoreCentral:
            case ApplicationSettingsType.CoreGlobal:
                return ApplicationSettingsTypeGroup.Core;
            case ApplicationSettingsType.DocumentLocal:
            case ApplicationSettingsType.DocumentCentral:
            case ApplicationSettingsType.DocumentGlobal:
                return ApplicationSettingsTypeGroup.Document;
            case ApplicationSettingsType.MediaBankLocal:
            case ApplicationSettingsType.MediaBankCentral:
            case ApplicationSettingsType.MediaBankGlobal:
                return ApplicationSettingsTypeGroup.MediaBank;
            case ApplicationSettingsType.PostLocal:
            case ApplicationSettingsType.PostCentral:
            case ApplicationSettingsType.PostGlobal:
                return ApplicationSettingsTypeGroup.Post;
            case ApplicationSettingsType.Appearance:
                return ApplicationSettingsTypeGroup.Appearance;
            case ApplicationSettingsType.Statistics2:
                return ApplicationSettingsTypeGroup.Statistics;
            case ApplicationSettingsType.Task:
                return ApplicationSettingsTypeGroup.Task;
            case ApplicationSettingsType.Email:
                return ApplicationSettingsTypeGroup.Email;
            case ApplicationSettingsType.SMS:
                return ApplicationSettingsTypeGroup.SMS;
            case ApplicationSettingsType.Forum:
            case ApplicationSettingsType.ForumAdmin:
                return ApplicationSettingsTypeGroup.Forum;
            case ApplicationSettingsType.Chat:
                return ApplicationSettingsTypeGroup.Chat;

            case ApplicationSettingsType.TaskV2Local:
            case ApplicationSettingsType.TaskV2Central:
            case ApplicationSettingsType.TaskV2Global:
            case ApplicationSettingsType.TaskV2Reports:
                return ApplicationSettingsTypeGroup.TaskV2;

            case ApplicationSettingsType.OnboardingLocal:
            case ApplicationSettingsType.OnboardingCentral:
            case ApplicationSettingsType.OnboardingGlobal:
            case ApplicationSettingsType.OnboardingReports:
                return ApplicationSettingsTypeGroup.Onboarding;

            case ApplicationSettingsType.ChecklistLocal:
            case ApplicationSettingsType.ChecklistCentral:
            case ApplicationSettingsType.ChecklistGlobal:
            case ApplicationSettingsType.CheckListReports:
                return ApplicationSettingsTypeGroup.Checklist;

            case ApplicationSettingsType.ChecklistV2Local:
            case ApplicationSettingsType.ChecklistV2Central:
            case ApplicationSettingsType.ChecklistV2Global:
            case ApplicationSettingsType.ChecklistV2Reports:
                return ApplicationSettingsTypeGroup.ChecklistV2;

            case ApplicationSettingsType.ChecklistV2AuditLocal:
            case ApplicationSettingsType.ChecklistV2AuditCentral:
            case ApplicationSettingsType.ChecklistV2AuditGlobal:
            case ApplicationSettingsType.ChecklistV2AuditReports:
                return ApplicationSettingsTypeGroup.ChecklistV2Audit;

            case ApplicationSettingsType.FormLocal:
            case ApplicationSettingsType.FormCentral:
            case ApplicationSettingsType.FormGlobal:
            case ApplicationSettingsType.FormReports:
                return ApplicationSettingsTypeGroup.Form;
            case ApplicationSettingsType.CustomFranchiseIntranetUpgrade:
                return ApplicationSettingsTypeGroup.Upgrade;
            case ApplicationSettingsType.CustomerBilling:
                return ApplicationSettingsTypeGroup.CustomerBilling;
            case ApplicationSettingsType.Tag:
                return ApplicationSettingsTypeGroup.Tag;
            case ApplicationSettingsType.LoginReminder:
                return ApplicationSettingsTypeGroup.LoginReminder;
            case ApplicationSettingsType.Personalkollen:
                return ApplicationSettingsTypeGroup.Personalkollen;
            case ApplicationSettingsType.BalooELearning:
                return ApplicationSettingsTypeGroup.BalooELearning;
            case ApplicationSettingsType.ConfidentialAgreement:
                return ApplicationSettingsTypeGroup.ConfidentialAgreement;
            case ApplicationSettingsType.BillingLocal:
            case ApplicationSettingsType.BillingCentral:
                return ApplicationSettingsTypeGroup.Billing;
            case ApplicationSettingsType.ADIntegration:
                return ApplicationSettingsTypeGroup.ADIntegration;
            case ApplicationSettingsType.Planday:
                return ApplicationSettingsTypeGroup.Planday;
            default:
                return ApplicationSettingsTypeGroup.Other;
        }
    }

    getApplicationSettingsTypeGroupLabels(
        applicationSettingTypeGroup: ApplicationSettingsTypeGroup,
    ): IApplicationSettingsTypeGroupLabels {
        const astg = ApplicationSettingsTypeGroup;
        switch (applicationSettingTypeGroup) {
            case astg.Core:
                return {
                    name: this.appTextService.get('common.our_organisation'),
                    intro: this.appTextService.get('role.intro_core'),
                    iconClass: 'far fa-user',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.core_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.write'),
                            description: this.appTextService.get('access_right.core_group_write_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.core_group_administrate_description'),
                        },
                    ],
                };
            case astg.Document:
                return {
                    name: this.appTextService.get('common.pages'),
                    intro: this.appTextService.get('role.intro_pages'),
                    iconClass: 'far fa-file-alt',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.pages_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.pages_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.pages_group_admin_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.super_admin'),
                            description: this.appTextService.get('access_right.pages_group_super_admin_description'),
                        },
                    ],
                };
            case astg.MediaBank:
                return {
                    name: this.appTextService.get('common.files'),
                    intro: this.appTextService.get('role.intro_media_bank'),
                    iconClass: 'far fa-file-image',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.media_bank_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.media_bank_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.super_admin'),
                            description: this.appTextService.get(
                                'access_right.media_bank_group_super_admin_description',
                            ),
                        },
                    ],
                };
            case astg.Post:
                return {
                    name: this.appTextService.get('posts'),
                    intro: this.appTextService.get('role.intro_post'),
                    iconClass: 'fas fa-bullhorn',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.post_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.post_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.post_group_admin_description'),
                        },
                    ],
                };
            case astg.Appearance:
                return {
                    name: this.appTextService.get('common.appearance'),
                    intro: this.appTextService.get('role.intro_appearance'),
                    iconClass: 'fas fa-paint-roller',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.appearance_group_admin_description'),
                        },
                    ],
                };
            case astg.Statistics:
                return {
                    name: this.appTextService.get('common.statistics'),
                    intro: this.appTextService.get('role.intro_statistics'),
                    iconClass: 'fas fa-chart-bar',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('access_level.local'),
                            description: this.appTextService.get('access_right.statistics_group_local_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.central'),
                            description: this.appTextService.get('access_right.statistics_group_central_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.global'),
                            description: this.appTextService.get('access_right.statistics_group_admin_description'),
                        },
                    ],
                };
            case astg.Task:
                return {
                    name: this.appTextService.get('task.tasks'),
                    intro: this.appTextService.get('role.intro_task'),
                    iconClass: 'fa fa-check-circle-o',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('access_level.local'),
                            description: this.appTextService.get('access_right.task_group_local_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.central'),
                            description: this.appTextService.get('access_right.task_group_central_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.task_group_admin_description'),
                        },
                    ],
                };
            case astg.TaskV2:
                return {
                    name: this.appTextService.get('task.tasks'),
                    intro: this.appTextService.get('role.intro_task_v2'),
                    iconClass: 'fa-regular fa-clipboard-check',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.task_v2_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.task_v2_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('reports'),
                            description: this.appTextService.get('access_right.task_group_reports_description'),
                        },
                    ],
                };
            case astg.Onboarding:
                return {
                    name: this.appTextService.get('onboarding.onboarding'),
                    intro: this.appTextService.get('role.intro_onboarding'),
                    iconClass: 'fa-regular fa-file-certificate',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.onboarding_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.onboarding_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get(
                                'access_right.onboarding_group_administrate_description',
                            ),
                        },
                        {
                            title: this.appTextService.get('reports'),
                            description: this.appTextService.get('access_right.onboarding_group_reports_description'),
                        },
                    ],
                };
            case astg.Email:
                return {
                    name: this.appTextService.get('common.email'),
                    intro: this.appTextService.get('role.intro_email'),
                    iconClass: 'far fa-envelope',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.email_group_read_description'),
                        },
                    ],
                };
            case astg.SMS:
                return {
                    name: this.appTextService.get('sms_object_name'),
                    intro: this.appTextService.get('role.intro_sms'),
                    iconClass: 'fas fa-sms',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.sms_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.sms_group_admin_description'),
                        },
                    ],
                };
            case astg.Forum:
                return {
                    name: this.appTextService.get('forum.forums'),
                    intro: this.appTextService.get('role.intro_forum'),
                    iconClass: 'far fa-comment',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.forum_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.forum_group_admin_description'),
                        },
                    ],
                };
            case astg.Chat:
                return {
                    name: this.appTextService.get('chat.chat'),
                    intro: this.appTextService.get('role.intro_chat'),
                    iconClass: 'far fa-comments',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('access_level.local'),
                            description: this.appTextService.get('access_right.chat_group_local_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.central'),
                            description: this.appTextService.get('access_right.chat_group_central_description'),
                        },
                        {
                            title: this.appTextService.get('access_level.global'),
                            description: this.appTextService.get('access_right.chat_group_global_description'),
                        },
                    ],
                };
            case astg.Checklist:
                return {
                    name: this.appTextService.get('checklist.module_name'),
                    intro: this.appTextService.get('role.intro_checklist'),
                    iconClass: 'far fa-check-square',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.checklist_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.checklist_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.checklist_group_admin_description'),
                        },
                        {
                            title: this.appTextService.get('reports'),
                            description: this.appTextService.get('access_right.checklist_group_reports_description'),
                        },
                    ],
                };
            case astg.ChecklistV2:
                return {
                    name: this.appTextService.get('checklist.checklists_v2'),
                    intro: this.appTextService.get('role.intro_checklist'),
                    iconClass: 'far fa-ballot-check',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.checklist_v2_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.checklist_v2_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.checklist_v2_group_admin_description'),
                        },
                        {
                            title: this.appTextService.get('reports'),
                            description: this.appTextService.get('access_right.checklist_group_reports_description'),
                        },
                    ],
                };
            case astg.ChecklistV2Audit:
                return {
                    name: this.appTextService.get('checklist.checklists_audit_v2'),
                    intro: this.appTextService.get('role.intro_checklists_v2_audit'),
                    iconClass: 'fa-solid fa-person-chalkboard',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get(
                                'access_right.checklist_v2_audit_group_read_description',
                            ),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get(
                                'access_right.checklist_v2_audit_group_share_description',
                            ),
                        },
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get(
                                'access_right.checklist_v2_audit_group_admin_description',
                            ),
                        },
                        {
                            title: this.appTextService.get('reports'),
                            description: this.appTextService.get(
                                'access_right.checklist_v2_audit_group_reports_description',
                            ),
                        },
                    ],
                };
            case astg.Form:
                return {
                    name: this.appTextService.get('form.module_name'),
                    intro: this.appTextService.get('role.intro_form'),
                    iconClass: 'far fa-clipboard',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.read'),
                            description: this.appTextService.get('access_right.form_group_read_description'),
                        },
                        {
                            title: this.appTextService.get('common.share'),
                            description: this.appTextService.get('access_right.form_group_share_description'),
                        },
                        {
                            title: this.appTextService.get('reports'),
                            description: this.appTextService.get('access_right.form_group_reports_description'),
                        },
                    ],
                };
            case astg.Upgrade:
                return {
                    name: this.appTextService.get('upgrade.upgrades'),
                    intro: this.appTextService.get('role.intro_upgrade'),
                    iconClass: 'far fa-lightbulb',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.upgrade_group_admin_description'),
                        },
                    ],
                };
            case astg.CustomerBilling:
                return {
                    name: this.appTextService.get('billing.customer_billing'),
                    intro: this.appTextService.get('role.intro_customer_billing'),
                    iconClass: 'far fa-money-bill-alt',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get(
                                'access_right.customer_billing_group_admin_description',
                            ),
                        },
                    ],
                };
            case astg.Tag:
                return {
                    name: this.appTextService.get('common.tag'),
                    intro: this.appTextService.get('role.intro_tag'),
                    iconClass: 'fas fa-hashtag',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.tag_group_admin_description'),
                        },
                    ],
                };
            case astg.LoginReminder:
                return {
                    name: this.appTextService.get('login.login_reminder'),
                    intro: this.appTextService.get('role.intro_login_reminder'),
                    iconClass: 'fa-regular fa-alarm-clock',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.login_reminder_group_admin_description'),
                        },
                    ],
                };
            case astg.Personalkollen:
                return {
                    name: this.appTextService.get('pk.personalkollen'),
                    intro: this.appTextService.get('role.intro_pk'),
                    iconClass: 'fas fa-user-shield',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.pk_group_admin_description'),
                        },
                    ],
                };
            case astg.BalooELearning:
                return {
                    name: this.appTextService.get('baloo.baloo'),
                    intro: this.appTextService.get('baloo.role_intro'),
                    iconClass: 'fa-file-certificate fa-regular',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('baloo_access_level.member'),
                            description: this.appTextService.get('access_right.baloo_member_description'),
                        },
                        {
                            title: this.appTextService.get('baloo_access_level.manager'),
                            description: this.appTextService.get('access_right.baloo_manager_description'),
                        },
                        {
                            title: this.appTextService.get('baloo_access_level.administrator'),
                            description: this.appTextService.get('access_right.baloo_admin_description'),
                        },
                    ],
                };
            case astg.Planday:
                return {
                    name: this.appTextService.get('planday.planday'),
                    intro: this.appTextService.get('planday.role_intro'),
                    iconClass: 'fas fa-user-shield',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('access_right.planday_admin_description'),
                        },
                    ],
                };
            case astg.ConfidentialAgreement:
                return {
                    name: this.appTextService.get('agreement.confidencial_agreement'),
                    intro: '',
                    iconClass: 'fas fa-handshake',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('agreement.confidencial_admin_rights'),
                        },
                    ],
                };
            case astg.Billing:
                return {
                    name: this.appTextService.get('billing.billing'),
                    intro: this.appTextService.get('billing intro todo'),
                    iconClass: 'far fa-money-bill-alt',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('billing administrate description todo'),
                        },
                    ],
                };
            case astg.ADIntegration:
                return {
                    name: this.appTextService.get('ad.role_name'),
                    intro: this.appTextService.get('ad.role_intro'),
                    iconClass: 'far ad',
                    rightsDescriptions: [
                        {
                            title: this.appTextService.get('common.administrate'),
                            description: this.appTextService.get('ad.role_description'),
                        },
                    ],
                };
        }
    }

    getRoleApplicationSettingGroups(roleApplicationSettings: ApplicationSettingsModel[]): {
        [prop: string]: ApplicationSettingsModel[];
    } {
        roleApplicationSettings.sort(
            comparator((item) => {
                let index = ApplicationSettingTypeOrder.indexOf(item.ApplicationSettingType);

                if (index == -1) index = 1000;

                return index;
            }),
        );

        const result: { [prop: string]: ApplicationSettingsModel[] } = {};
        roleApplicationSettings.forEach((roleApplicationSetting) => {
            const group = this.getAccessRightTypeGroup(roleApplicationSetting.ApplicationSettingType);
            if (!result[group]) {
                result[group] = [];
            }
            result[group].push(roleApplicationSetting);
        });
        return result;
    }

    isModuleRightChecked(applicationSettingsModels: ApplicationSettingsModel[]): boolean {
        if (this.isIndependentModuleRightsGroups(applicationSettingsModels[0].ApplicationSettingType)) {
            return applicationSettingsModels.some((applicationSettingsModel: ApplicationSettingsModel) => {
                return this.isApplicationSettingsModelAvailable(applicationSettingsModel);
            });
        }

        return applicationSettingsModels.every((applicationSettingsModel: ApplicationSettingsModel) => {
            return this.isApplicationSettingsModelAvailable(applicationSettingsModel);
        });
    }

    private independentModuleRightsGroups: ApplicationSettingsTypeGroup[] = [
        ApplicationSettingsTypeGroup.Core,
        ApplicationSettingsTypeGroup.Document,
        ApplicationSettingsTypeGroup.MediaBank,
        ApplicationSettingsTypeGroup.Post,
        ApplicationSettingsTypeGroup.Form,
        ApplicationSettingsTypeGroup.Checklist,
        ApplicationSettingsTypeGroup.ChecklistV2,
        ApplicationSettingsTypeGroup.ChecklistV2Audit,
        ApplicationSettingsTypeGroup.Forum,
        ApplicationSettingsTypeGroup.TaskV2,
        ApplicationSettingsTypeGroup.Onboarding,
    ];

    isIndependentModuleRightsGroups(applicationType: ApplicationSettingsType): boolean {
        return this.independentModuleRightsGroups.some((groupType) => {
            return groupType === this.getAccessRightTypeGroup(applicationType);
        });
    }

    isApplicationSettingsModelAvailable(applicationSettingsModel: ApplicationSettingsModel): boolean {
        return applicationSettingsModel.AccessRightType !== AccessRightType.UnAvailable;
    }

    hasAccessToApplicationSetting(
        roleApplicationSettings: ApplicationSettingsModel[],
        applicationSettingType: ApplicationSettingsType,
        minAccessRightType: AccessRightType,
    ) {
        const targetRoleApplicationSetting = roleApplicationSettings.find(
            (roleApplicationSetting) => roleApplicationSetting.ApplicationSettingType === applicationSettingType,
        );
        return (
            isDefined(targetRoleApplicationSetting) &&
            targetRoleApplicationSetting.AccessRightType >= minAccessRightType
        );
    }

    hasAdministrationOnCoreGlobal(roleApplicationSettings: ApplicationSettingsModel[]) {
        return this.hasAccessToApplicationSetting(
            roleApplicationSettings,
            ApplicationSettingsType.CoreGlobal,
            AccessRightType.Admin,
        );
    }

    hasNoRightsOnCore(roleApplicationSettings: ApplicationSettingsModel[]) {
        return !this.hasAccessToApplicationSetting(
            roleApplicationSettings,
            ApplicationSettingsType.CoreLocal,
            AccessRightType.Read,
        );
    }

    roleHasManageStaffRights(role: RoleModel) {
        const hasWriteOnCoreLocal = this.hasAccessToApplicationSetting(
            role.RoleApplicationSettings,
            ApplicationSettingsType.CoreLocal,
            AccessRightType.Write,
        );
        return hasWriteOnCoreLocal;
    }

    getExtendedRoleRightsValidationErrors(
        role: RoleModel,
        extendedRoleRight: ExtendedRoleRightModel,
    ): ValidationErrors {
        return {
            access_roles_required:
                !!role.Id && this.roleHasManageStaffRights(role) && !extendedRoleRight.ExtendedAccessRoleIds.length,
        };
    }
}
